import { Provider } from './../../models/Provider';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

import { Control } from './../../models/Control';
import { UserCreditInfo } from './../../models/UserCreditInfo';

export interface ChartData {
  data: number[];
  labels: string[];
}

/**
 * Série RCS do gráfico (17tm466frtx). Vem no `GET /daily-sents` só para conta com o RCS
 * liberado; cada array tem um ponto por dia de `days`, alinhado com `labels`/`data`.
 * Regras de contagem em `docs/rcs/DASHBOARD.md` do backend.
 */
export interface RcsDailySeries {
  days: string[];
  sent: number[];
  delivered: number[];
  read: number[];
  clicks: number[];
  sms_fallback: number[];
  totals: { sent: number; delivered: number; read: number; clicks: number; sms_fallback: number };
  /**
   * Há retorno do broker (entregue/lido)? `false` (02/11/2026, `RCS_PROVIDER_WEBHOOKS_ENABLED`
   * desligado): o RCS conta como enviado no aceite, não há entregue nem lido e o clique é o do
   * link curto. Ausente (backend antigo) vale como `true`.
   */
  provider_status?: boolean;
}

/** Resposta do `GET /daily-sents`: a série de SMS e, para quem tem RCS, o bloco `rcs`. */
export interface DailySents extends ChartData {
  rcs?: RcsDailySeries;
}

export interface CallRecord {
  name: string;
  phone: string;
  sent_at: string;
  status: string;
  call_duration_seconds: number | null;
  listened_percentage: number | null;
  origin_name?: string;
  campaign_name?: string;
  broadcast_name?: string;
}

export interface CallsReportDay {
  counter_date: string;
  quantity: number;
}

export interface PaginatedCalls {
  data: CallRecord[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

@Injectable({ providedIn: 'root' })
export class DashboardService {

  constructor(private http: HttpClient) { }

  userCreditsInfo(): Observable<UserCreditInfo> {
    return this.http.get<UserCreditInfo>(`${environment.apiUrl}/user-credits-info`);
  }

  lastMessages(): Observable<Control[]> {
    return this.http.get<Control[]>(`${environment.apiUrl}/last-messages`);
  }

  dailySents(startDate: string): Observable<DailySents> {
    return this.http.get<DailySents>(`${environment.apiUrl}/daily-sents`, {
      params: {
        startDate
      }
    });
  }

  dailyCallSents(startDate: string): Observable<ChartData> {
    return this.http.get<ChartData>(`${environment.apiUrl}/daily-call-sents`, {
      params: {
        startDate
      }
    });
  }

  dailyWhatsappSents(startDate: string): Observable<ChartData> {
    return this.http.get<ChartData>(`${environment.apiUrl}/daily-whatsapp-sents`, {
      params: {
        startDate
      }
    });
  }

  lastCalls(): Observable<CallRecord[]> {
    return this.http.get<CallRecord[]>(`${environment.apiUrl}/last-calls`);
  }

  callsReport(month: number, year: number): Observable<CallsReportDay[]> {
    return this.http.get<CallsReportDay[]>(`${environment.apiUrl}/calls-report`, {
      params: { month: month.toString(), year: year.toString() }
    });
  }

  callsPaginated(date: string, page: number, perPage: number): Observable<PaginatedCalls> {
    return this.http.get<PaginatedCalls>(`${environment.apiUrl}/calls`, {
      params: { date, page: page.toString(), per_page: perPage.toString() }
    });
  }

  callsBroadcastPaginated(date: string, page: number, perPage: number): Observable<PaginatedCalls> {
    return this.http.get<PaginatedCalls>(`${environment.apiUrl}/calls-broadcast`, {
      params: { date, page: page.toString(), per_page: perPage.toString() }
    });
  }

  updateProvider(provider: Provider): Observable<any> {
    return this.http.put(`${environment.apiUrl}/provider/${provider.id}`, provider);
  }

}
